import * as React from "react";

import { Button, ButtonProps } from "@/components/frontend/ui/button";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon } from "lucide-react";
import "./style.scss";

export function PaginationRoot({ className, ...props }: React.ComponentProps<"nav">) {
	return <nav role="navigation" aria-label="pagination" data-slot="pagination" className={cn("mx-auto flex w-full justify-center", className)} {...props} />;
}

export function PaginationContent({ className, ...props }: React.ComponentProps<"ul">) {
	return <ul data-slot="pagination-content" className={cn("gap-2 flex items-center", className)} {...props} />;
}

export function PaginationItem({ ...props }: React.ComponentProps<"li">) {
	return <li data-slot="pagination-item" {...props} />;
}

export interface PaginationLinkProps extends ButtonProps {
	active?: boolean;
}

export function PaginationLink({ children, active = false, ...props }: PaginationLinkProps) {
	return (
		<Button active={active} aria-current={active ? "page" : undefined} data-slot="pagination-link" data-active={active} {...props}>
			{children}
		</Button>
	);
}

export function PaginationPrevious({ className, text = "Previous", ...props }: Omit<PaginationLinkProps, "children"> & { text?: string }) {
	const isDisabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
	if (isDisabled) return null;

	return (
		<PaginationLink aria-label="Go to previous page" className={cn("pl-1.5!", className)} {...props}>
			<ChevronLeftIcon data-icon="inline-start" className="cn-rtl-flip" />
			<span className="hidden sm:block">{text}</span>
		</PaginationLink>
	);
}

export function PaginationNext({ className, text = "Next", ...props }: Omit<PaginationLinkProps, "children"> & { text?: string }) {
	const isDisabled = props.disabled || props["aria-disabled"] === true || props["aria-disabled"] === "true";
	if (isDisabled) return null;

	return (
		<PaginationLink aria-label="Go to next page" className={cn("pr-1.5!", className)} {...props}>
			<span className="hidden sm:block">{text}</span>
			<ChevronRightIcon data-icon="inline-end" className="cn-rtl-flip" />
		</PaginationLink>
	);
}

export function PaginationEllipsis({ className, label = "More pages", ...props }: React.ComponentProps<"span"> & { label?: string }) {
	return (
		<span
			aria-hidden
			data-slot="pagination-ellipsis"
			className={cn("size-8 [&_svg:not([class*='size-'])]:size-4 flex items-center justify-center", className)}
			{...props}
		>
			<MoreHorizontalIcon />
			<span className="sr-only">{label}</span>
		</span>
	);
}
